<template>
  <div class="w-100 h-100">
    <v-breadcrumbs :items="items">
      <template v-slot:divider>
        <v-icon>mdi-{{ $t("breadcrumb.icon") }}</v-icon>
      </template>
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item
          :id="`breadcrumb-${item.id}`"
          exact-path
          exact-active-class="active"
          :to="{ name: item.componentName }"
        >
          {{ $t("breadcrumb." + item.text) }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>
    <template>
      <p class="note" v-if="showNote">
        {{ $t("breadcrumb." + note) }}
      </p>
      <slot name="note" v-else></slot>
    </template>
    <template>
      <div class="d-flex gap-2" v-if="showActions">
        <button id="breadcrumb-back-btn" class="back-btn" @click="previousPage" v-if="!hideBackButton">
          {{ $t("breadcrumb.back") }}
        </button>
        <button
          id="breadcrumb-next-btn"
          class="next-btn"
          @click="nextPage"
          v-if="!hideNextButton"
          :disabled="isNextDisabled"
        >
          {{ $t("breadcrumb.next") }}
        </button>
        <button
          id="breadcrumb-finish-btn"
          class="finish-btn"
          @click="finishSteps"
          v-if="$route.name === 'ScheduleInterview'"
          :disabled="isNextDisabled"
        >
          {{ $t("breadcrumb.finish") }}
        </button>
      </div>
      <slot name="actions" v-else></slot>
    </template>
  </div>
</template>

<script>
import GlobalMixin from "../../GlobalMixin";
import KidsReservationMixin from "../../mixins/KidsReservationMixin";

export default {
  name: "Breadcrumb",
  mixins: [GlobalMixin, KidsReservationMixin],
  props: {
    items: {
      type: Array,
      required: true,
    },
    icon: {
      type: String,
      default: "slash-forward",
      required: false,
    },
    showNote: {
      type: Boolean,
      default: false,
      required: false,
    },
    note: {
      type: String,
      default: "",
      required: false,
    },
    showActions: {
      type: Boolean,
      default: false,
      required: false,
    },
    color: {
      type: String,
      default: "#1976d2",
      required: false,
    },
    disabledColor: {
      type: String,
      default: "#000",
      required: false,
    },
  },
  /**
   * Initialize component
   */
  mounted() {
    const items = Array.from(document.querySelectorAll(".v-breadcrumbs__item"));
    let color = this.color;
    let disabled = false;
    items.forEach((item) => {
      item.style.color = color;
      if (disabled) item.classList.add("v-breadcrumbs__item--disabled");
      if (item.classList.contains("active")) {
        disabled = true;
        color = this.disabledColor;
      }
    });
  },
  methods: {
    /**
     * Navigate to next page
     */
    nextPage() {
      const index = this.items.findIndex(
        (obj) => obj.componentName === this.$route.name
      );
      if (index === -1) return;
      const newIndex = index + 1;
      // Perform a method before leaving current view to next view
      const componentName = this.$route.name;
      switch (componentName) {
        case "KidsCourses":
          this.addInterestedKidsCourses();
          break;
        case "Courses":
          if (!this.$store.getters.isAuthenticated) {
            this.openLoginModal();
            return;
          }
      }
      this.$router.push({ name: this.items[newIndex].componentName });
    },
    /**
     * Navigate to previous page and delete state for current page
     */
    previousPage() {
      const index = this.items.findIndex(
        (obj) => obj.componentName === this.$route.name
      );
      const newIndex = index - 1;
      if (newIndex === -1) return;
      // Remove state of current view and go back to previous page
      const componentName = this.$route.name;
      switch (componentName) {
        case "ProgramsList":
          !this.$store.dispatch("saveProgram", null);
          break;
        case "SessionsList":
          !this.$store.dispatch("removeSelectedSlot");
          break;
        case "ScheduleInterview":
          !this.$store.dispatch("removeWebinar");
          break;
      }
      this.$router.push({ name: this.items[newIndex].componentName });
    },
    /**
     * Open Login modal
     */
    openLoginModal() {
      window.dispatchEvent(
        new CustomEvent("loginModalChange", {
          detail: {
            opened: true,
            tab: 0,
          },
        })
      );
    },
    /**
     * Finish all steps and end breadcrumb
     */
    finishSteps() {
      this.$emit("finishBreadcrumb");
    },
  },
  computed: {
    /**
     * Hide back button if first router
     */
    hideBackButton() {
      return this.$route.name === this.items[0].componentName;
    },
    /**
     * Hide next button if last router
     */
    hideNextButton() {
      return this.$route.name === this.items.at(-1).componentName;
    },
    /**
     * Disable next button if data is not provided
     */
    isNextDisabled() {
      const componentName = this.$route.name;
      switch (componentName) {
        case "Courses":
          return !this.$store.getters.GET_SELECTED_COURSES.length;
        case "ProgramsList":
          return !this.$store.getters.GET_SELECTED_PROGRAM;
        case "SessionsList":
          return !this.$store.getters.GET_SELECTED_SLOTS.length;
        case "KidsCoursesList":
          return !this.$store.getters.GET_SELECTED_KIDS_COURSES.length;
        case "KidsCourses":
          return !this.$store.getters.GET_SELECTED_MATRIX.length;
        case "ScheduleInterview":
          return !this.$store.getters.GET_WEBINAR_ID;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.note {
  margin: 8px 0;
  font-weight: 400;
  font-size: 12px;
  line-height: 24px;
  color: $paragraph;
}
.next-btn,
.finish-btn {
  padding: 8px 34px;
  color: $white;
  background: $green;
  border-radius: 4px;
  &:hover {
    background: $hover-green;
  }
  &:disabled {
    background: #b1afaf;
  }
}
.back-btn {
  border: 1px solid $green;
  border-radius: 4px;
  color: $green;
  padding: 8px 34px;
  &:hover {
    background: $green;
    color: $white;
  }
}
::v-deep .v-breadcrumbs__item {
  &.v-breadcrumbs__item--disabled {
    pointer-events: none;
  }
  &:hover {
    color: $green;
  }
}
@media all and (min-width: $min-md-screen) and (max-width: $max-md-screen) {
  .note {
    margin: 12px 0 8px;
  }
  .next-btn,
  .back-btn {
    padding: 10px 34px;
  }
}

@media all and (max-width: $min-md-screen) {
  .note {
    margin: 8px 0;
  }
}
</style>
